/**
 * WritingFeedbackPanel, shared writing correction display components.
 *
 * Extracted from ExamResultsPage so they can be reused in
 * SchreibenTrainingPage (practice writing) without duplication.
 *
 * Exports:
 *   sanitizeCorrectedText    – strips API artefact junk from correctedText
 *   SingleTaskScores         – per-criterion score bars for one writing task
 *   WritingFeedbackDisplay   – full feedback card (handles single / multi-task)
 *   PracticeWritingFeedback  – rich teacher-style analysis for practice page
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, ChevronDown } from 'lucide-react';
import TeacherCorrections from '../tasks/writing/TeacherCorrections.jsx';

// ---------------------------------------------------------------------------
// Error-type config shared by PracticeWritingFeedback
// ---------------------------------------------------------------------------
const ERROR_CFG = {
  spelling:           { color: '#dc2626', bg: '#fef2f2', border: '#fca5a5', label: 'Rechtschreibung' },
  grammar:            { color: '#2563eb', bg: '#eff6ff', border: '#93c5fd', label: 'Grammatik' },
  punctuation:        { color: '#ea580c', bg: '#fff7ed', border: '#fed7aa', label: 'Zeichensetzung' },
  'word-choice':      { color: '#7c3aed', bg: '#f5f3ff', border: '#c4b5fd', label: 'Wortwahl' },
  'sentence-structure':{ color: '#059669', bg: '#ecfdf5', border: '#6ee7b7', label: 'Satzbau' },
  style:              { color: '#6b7280', bg: '#f9fafb', border: '#d1d5db', label: 'Stil' },
};
const errCfg = (type) => ERROR_CFG[type] || { color: '#6b7280', bg: '#f9fafb', border: '#d1d5db', label: 'Sonstige' };

const GLOBAL_SUMMARY_FIELDS = new Set([
  'overallScore','scaledScore','criticalFailure','taskTotal',
  'task1Total','task2Total','task3Total','passed','rawScore',
  'knockoutApplied','meetsMinimumWordCount','passedTask','wordCount',
]);

// ---------------------------------------------------------------------------
// Utility
// ---------------------------------------------------------------------------
export const sanitizeCorrectedText = (text) => {
  if (typeof text !== 'string') return text || '';
  return text
    .trim()
    .replace(/\s*[)}\]]+\s*$/g, '')
    .replace(/^\s*[{\[(\s]+\s*/g, '')
    .trim();
};

// ---------------------------------------------------------------------------
// PracticeWritingFeedback, rich teacher-style analysis card for practice page
// ---------------------------------------------------------------------------
export const PracticeWritingFeedback = ({ feedback, writingData }) => {
  const [showCorrected, setShowCorrected] = useState(false);
  const { t } = useTranslation();

  if (!feedback) return null;

  const scores        = feedback.scores || {};
  const feedbackTexts = feedback.feedback || {};
  const correctedText = sanitizeCorrectedText(feedback.correctedText || '');
  const corrections   = feedback.corrections || [];
  const guideline     = feedback.guidelineMetadata || {};
  const textData      = writingData?.texts?.[Object.keys(writingData?.texts || {})[0]];

  // ── Criteria score entries (exclude summary fields) ──────────────────────
  const allEntries = Object.entries(scores).filter(
    ([k]) => !GLOBAL_SUMMARY_FIELDS.has(k) && scores[k] !== null && scores[k] !== undefined,
  );
  const pointKeys = new Set(
    allEntries.map(([k]) => k).filter(k => k.endsWith('Points')).map(k => k.replace('Points', '')),
  );
  const criteriaEntries = allEntries.filter(([k]) => !pointKeys.has(k));

  const criteriaMaxMap = { ...(guideline.criteriaMaxPoints || {}) };
  let maxScore = guideline.maxPoints || 45;

  let totalScore = 0;
  if (scores.scaledScore !== undefined) {
    totalScore = Number(scores.scaledScore) || 0;
  } else if (scores.taskTotal !== undefined) {
    totalScore = Number(scores.taskTotal) || 0;
    maxScore   = guideline.maxPoints || 15;
  } else {
    criteriaEntries.forEach(([, v]) => { totalScore += Number(v) || 0; });
  }

  criteriaEntries.forEach(([key]) => {
    if (!criteriaMaxMap[key]) {
      criteriaMaxMap[key] =
        guideline.scoringCriteria?.[key]?.maxPoints ||
        (criteriaEntries.length > 0 ? Math.round((maxScore / criteriaEntries.length) * 10) / 10 : 5);
    }
  });

  const displayTotal = Math.min(totalScore, maxScore);
  const pct          = maxScore > 0 ? Math.min(100, (displayTotal / maxScore) * 100) : 0;
  const passed       = feedback.passed;

  // ── Error stats grouped by type ──────────────────────────────────────────
  const errorGroups = corrections.reduce((acc, c) => {
    const type = c.type || 'unknown';
    if (!acc[type]) acc[type] = [];
    acc[type].push(c);
    return acc;
  }, {});
  const totalErrors = corrections.length;

  // ── Criterion display name ────────────────────────────────────────────────
  const criterionLabel = (key) => {
    const i18nKey  = `examResults.criteria.${key.replace('Points', '')}`;
    const translated = t(i18nKey);
    return translated !== i18nKey ? translated : key;
  };

  // ── Score-bar colour ──────────────────────────────────────────────────────
  const barColor = (p) => p >= 66 ? '#059669' : p >= 40 ? '#f59e0b' : '#dc2626';

  // ── Result banner accent ──────────────────────────────────────────────────
  const bannerClass = passed === true
    ? 'from-emerald-50 to-green-50 border-emerald-200'
    : passed === false
      ? 'from-red-50 to-rose-50 border-red-200'
      : 'from-purple-50 to-indigo-50 border-purple-200';
  const dotColor = passed === true ? 'bg-emerald-500' : passed === false ? 'bg-red-500' : 'bg-purple-600';
  const progressColor = passed === true ? 'bg-emerald-500' : passed === false ? 'bg-red-500' : 'bg-purple-600';

  return (
    <div className="space-y-4">

      {/* ── 1. Result banner ──────────────────────────────────────────────── */}
      <div className={`relative overflow-hidden rounded-2xl border-2 p-4 sm:p-5 bg-gradient-to-br ${bannerClass}`}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-white text-xl font-black shadow-sm flex-shrink-0 ${dotColor}`}>
              {passed === true ? '✓' : passed === false ? '✗' : '∑'}
            </div>
            <div>
              <p className="font-black text-2xl text-gray-900 leading-none" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {displayTotal % 1 === 0 ? displayTotal : displayTotal.toFixed(1)}
                <span className="text-base font-normal text-gray-400"> / {maxScore}</span>
              </p>
              <p className="text-sm font-semibold text-gray-500 mt-0.5">
                {pct.toFixed(0)}% ·{' '}
                {passed === true ? 'Bestanden' : passed === false ? 'Nicht bestanden' : 'Analyse abgeschlossen'}
              </p>
            </div>
          </div>
          {totalErrors > 0 && (
            <div className="flex flex-wrap gap-1.5 justify-end">
              {Object.entries(errorGroups).map(([type, list]) => {
                const cfg = errCfg(type);
                return (
                  <span
                    key={type}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border"
                    style={{ color: cfg.color, background: cfg.bg, borderColor: cfg.border }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: cfg.color }} />
                    {cfg.label} {list.length}
                  </span>
                );
              })}
            </div>
          )}
        </div>
        <div className="mt-4 h-1.5 bg-white/60 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-1000 ease-out ${progressColor}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* ── 2. Annotated text ─────────────────────────────────────────────── */}
      {textData?.text && (
        <div className="rounded-2xl border-2 border-black/10 bg-white overflow-hidden shadow-sm">
          <div className="px-4 py-2.5 border-b border-black/8 bg-gray-50/80 flex items-center justify-between flex-wrap gap-2">
            <h4 className="font-bold text-gray-800 text-sm">Dein Text, annotiert</h4>
            {totalErrors === 0 && (
              <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full">
                ✓ Keine Fehler gefunden
              </span>
            )}
          </div>
          <div className="p-4">
            <TeacherCorrections originalText={textData.text} corrections={corrections} />
          </div>
        </div>
      )}

      {/* ── 3. Criteria scores with feedback ──────────────────────────────── */}
      {criteriaEntries.length > 0 && (
        <div className="rounded-2xl border-2 border-black/10 bg-white overflow-hidden shadow-sm">
          <div className="px-4 py-2.5 border-b border-black/8 bg-gray-50/80">
            <h4 className="font-bold text-gray-800 text-sm">Bewertungskriterien</h4>
          </div>
          <div className="divide-y divide-black/5">
            {criteriaEntries.map(([key, score]) => {
              const num        = Number(score) || 0;
              const max        = criteriaMaxMap[key] || 5;
              const barPct     = max > 0 ? Math.min(100, (num / max) * 100) : 0;
              const color      = barColor(barPct);
              const label      = criterionLabel(key);
              const fbText     =
                feedbackTexts[key] ||
                feedbackTexts[label] ||
                Object.entries(feedbackTexts).find(([k]) =>
                  k.toLowerCase().includes(label.toLowerCase()) ||
                  label.toLowerCase().includes(k.toLowerCase())
                )?.[1] || '';

              return (
                <div key={key} className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-semibold text-gray-800 text-sm">{label}</p>
                    <div className="flex items-baseline gap-0.5 ml-2 flex-shrink-0">
                      <span className="font-black text-xl leading-none" style={{ color }}>
                        {num % 1 === 0 ? num : num.toFixed(1)}
                      </span>
                      <span className="text-gray-400 text-sm">/ {max}</span>
                    </div>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-3">
                    <div
                      className="h-full rounded-full transition-all duration-700 ease-out"
                      style={{ width: `${barPct}%`, background: color }}
                    />
                  </div>
                  {fbText && (
                    <p className="text-sm text-gray-600 leading-relaxed bg-gray-50 rounded-xl px-3 py-2.5 border border-gray-100">
                      {fbText}
                    </p>
                  )}
                </div>
              );
            })}
            {/* Any unmatched feedback texts */}
            {(() => {
              const matchedKeys = new Set(
                criteriaEntries.flatMap(([key]) => {
                  const label = criterionLabel(key);
                  return [key, label, label.toLowerCase()];
                })
              );
              const unmatched = Object.entries(feedbackTexts).filter(
                ([k]) => !matchedKeys.has(k) && !matchedKeys.has(k.toLowerCase())
              );
              return unmatched.map(([k, text]) => (
                <div key={k} className="p-4 border-t border-black/5">
                  <p className="font-semibold text-gray-700 text-sm mb-1">{k}</p>
                  <p className="text-sm text-gray-600 leading-relaxed bg-gray-50 rounded-xl px-3 py-2.5 border border-gray-100">
                    {text}
                  </p>
                </div>
              ));
            })()}
          </div>
        </div>
      )}

      {/* If no criteria but there are feedback texts, show them standalone */}
      {criteriaEntries.length === 0 && Object.keys(feedbackTexts).length > 0 && (
        <div className="rounded-2xl border-2 border-black/10 bg-white overflow-hidden shadow-sm">
          <div className="px-4 py-2.5 border-b border-black/8 bg-gray-50/80">
            <h4 className="font-bold text-gray-800 text-sm">Lehrerkommentar</h4>
          </div>
          <div className="divide-y divide-black/5">
            {Object.entries(feedbackTexts).map(([k, text]) => (
              <div key={k} className="p-4">
                <p className="font-semibold text-gray-700 text-sm mb-1">{k}</p>
                <p className="text-sm text-gray-600 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── 4. Collapsible corrected version ─────────────────────────────── */}
      {correctedText && (
        <div className="rounded-2xl border-2 border-black/10 bg-white overflow-hidden shadow-sm">
          <button
            onClick={() => setShowCorrected((v) => !v)}
            className="w-full px-4 py-3 flex items-center justify-between bg-gray-50/80 hover:bg-gray-100 transition-colors duration-150 touch-manipulation"
          >
            <h4 className="font-bold text-gray-800 text-sm">Korrigierte Fassung</h4>
            <ChevronDown
              className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${showCorrected ? 'rotate-180' : ''}`}
            />
          </button>
          {showCorrected && (
            <div className="p-4 border-t border-black/8">
              <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                {correctedText}
              </p>
            </div>
          )}
        </div>
      )}

    </div>
  );
};

// ---------------------------------------------------------------------------
// SingleTaskScores
// ---------------------------------------------------------------------------
export const SingleTaskScores = ({ scores, guidelineMetadata, taskNumber }) => {
  const { t } = useTranslation();
  if (!scores || Object.keys(scores).length === 0) return null;

  const taskHierarchy = guidelineMetadata?.taskHierarchy || [];

  const GLOBAL_SUMMARY_FIELDS = [
    'overallScore', 'scaledScore', 'criticalFailure', 'taskTotal',
    'task1Total', 'task2Total', 'task3Total',
    'passed', 'rawScore', 'knockoutApplied', 'meetsMinimumWordCount',
    'passedTask', 'wordCount',
  ];

  let criteriaMaxPointsMap = {};
  if (taskNumber && taskHierarchy.length > 0) {
    const taskInfo = taskHierarchy.find((ti) => ti.taskNumber === taskNumber);
    if (taskInfo?.criteriaMaxPoints) criteriaMaxPointsMap = taskInfo.criteriaMaxPoints;
  } else if (guidelineMetadata?.criteriaMaxPoints) {
    criteriaMaxPointsMap = guidelineMetadata.criteriaMaxPoints;
  }

  const getCategoryDisplayName = (key) => {
    const baseKey = key.replace('Points', '');
    const i18nKey = `examResults.criteria.${baseKey}`;
    const translated = t(i18nKey);
    return translated !== i18nKey ? translated : key;
  };

  const allScoreEntries = Object.entries(scores).filter(
    ([key]) => !GLOBAL_SUMMARY_FIELDS.includes(key) && scores[key] !== null && scores[key] !== undefined,
  );

  const keysWithPoints = new Set(
    allScoreEntries
      .map(([key]) => key)
      .filter((key) => key.endsWith('Points'))
      .map((key) => key.replace('Points', '')),
  );

  const criteriaScores = allScoreEntries.filter(([key]) => !keysWithPoints.has(key));

  const taskMax =
    taskNumber && taskHierarchy.length > 0
      ? taskHierarchy.find((ti) => ti.taskNumber === taskNumber)?.points
      : null;
  const numCriteria = criteriaScores.length;

  criteriaScores.forEach(([key]) => {
    if (!criteriaMaxPointsMap[key]) {
      const baseKey = key.replace('Points', '');
      const fromMap =
        criteriaMaxPointsMap[baseKey] ||
        guidelineMetadata?.scoringCriteria?.[key]?.maxPoints ||
        guidelineMetadata?.scoringCriteria?.[baseKey]?.maxPoints;
      if (fromMap != null) {
        criteriaMaxPointsMap[key] = fromMap;
      } else if (taskMax != null && numCriteria > 0) {
        criteriaMaxPointsMap[key] = Math.round((taskMax / numCriteria) * 10) / 10;
      } else {
        criteriaMaxPointsMap[key] = 5;
      }
    }
  });

  let totalScore = 0;
  let maxScore = 0;

  if (scores.scaledScore !== undefined) {
    totalScore = typeof scores.scaledScore === 'number' ? scores.scaledScore : parseFloat(scores.scaledScore) || 0;
    maxScore = guidelineMetadata?.maxPoints || 45;
  } else if (scores.taskTotal !== undefined) {
    totalScore = typeof scores.taskTotal === 'number' ? scores.taskTotal : parseFloat(scores.taskTotal) || 0;
    maxScore =
      taskNumber && taskHierarchy.length > 0
        ? taskHierarchy.find((ti) => ti.taskNumber === taskNumber)?.points || 15
        : guidelineMetadata?.maxPoints || 15;
  } else {
    criteriaScores.forEach(([criterion, score]) => {
      const numericScore = typeof score === 'number' ? score : parseFloat(score) || 0;
      totalScore += numericScore;
      maxScore += criteriaMaxPointsMap[criterion] || 0;
    });
  }

  const displayTotal = maxScore > 0 ? Math.min(totalScore, maxScore) : totalScore;
  const percentage = maxScore > 0 ? (displayTotal / maxScore) * 100 : 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h5 className="text-lg font-bold text-gray-800">{t('examResults.criteriaTitle')}</h5>
        <div className="text-right">
          <p className="text-purple-600 text-sm">{t('examResults.totalScore')}</p>
          <p className="text-2xl font-bold text-purple-800">
            {displayTotal.toFixed(2)}/{maxScore}
          </p>
          <p className="text-purple-600 text-sm">{percentage.toFixed(0)}%</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {criteriaScores.map(([category, score]) => {
          const numericScore = typeof score === 'number' ? score : parseFloat(score) || 0;
          const maxForCriterion = criteriaMaxPointsMap[category] || 5;
          const displayName = getCategoryDisplayName(category);
          const barPercent = maxForCriterion > 0 ? Math.min(100, (numericScore / maxForCriterion) * 100) : 0;

          return (
            <div key={category} className="relative overflow-hidden bg-white p-4 rounded-xl border border-gray-200">
              <div className="relative z-10">
                <p className="font-semibold text-gray-700 mb-2">{displayName}</p>
                <div className="flex items-end space-x-2">
                  <p className="text-2xl font-bold text-purple-600">{numericScore.toFixed(2)}</p>
                  <p className="text-gray-500 pb-1">/ {maxForCriterion}</p>
                </div>
                <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-1000 ease-out"
                    style={{ width: `${barPercent}%` }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// WritingFeedbackDisplay
// ---------------------------------------------------------------------------
export const WritingFeedbackDisplay = ({ feedback, writingData }) => {
  const { t } = useTranslation();
  if (!feedback) return null;

  const guidelineMetadata = feedback.guidelineMetadata || {};
  const hasTextCorrections = feedback.textCorrections && Object.keys(feedback.textCorrections).length > 0;
  const hasTaskEvaluations = feedback.task1Evaluation || feedback.task2Evaluation || feedback.task3Evaluation;

  // ── MULTI-TASK: textCorrections (OSD B2) ──────────────────────────────────
  if (hasTextCorrections) {
    const textKeys = Object.keys(feedback.textCorrections);
    return (
      <div className="space-y-8">
        {textKeys.map((textKey, index) => {
          const taskCorrection = feedback.textCorrections[textKey];
          const textData = writingData?.texts?.[textKey];

          if (!taskCorrection || taskCorrection.error) {
            return (
              <div key={textKey} className="bg-red-50 border border-red-200 rounded-xl p-6">
                <p className="text-red-600">{t('examResults.correctionErrorTask', { n: index + 1 })}</p>
              </div>
            );
          }

          const scores = taskCorrection.scores || {};
          const feedbackTexts = taskCorrection.feedback || {};
          const corrections = taskCorrection.corrections || [];

          return (
            <div key={textKey} className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl border border-purple-200 p-6 space-y-6">
              <div className="flex items-center justify-between border-b border-purple-200 pb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-purple-600 text-white rounded-xl">
                    <Sparkles className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-purple-800">
                      {textData?.partName || t('examResults.taskNumber', { n: index + 1 })}
                    </h4>
                    <p className="text-purple-600 text-sm">
                      {textData?.taskType?.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </p>
                  </div>
                </div>
                {Object.keys(scores).length > 0 && (
                  <div className="text-right">
                    <p className="text-purple-600 text-sm">{t('examResults.points')}</p>
                    <p className="text-2xl font-bold text-purple-800">
                      {(scores.taskTotal || scores.overallScore || 0).toFixed(2)}/15
                    </p>
                  </div>
                )}
              </div>

              {textData?.text && corrections.length > 0 && (
                <div>
                  <h5 className="text-lg font-bold text-gray-800 mb-3">
                    {t('examResults.yourTextWithCorrections')} ({corrections.length})
                  </h5>
                  <TeacherCorrections originalText={textData.text} corrections={corrections} />
                </div>
              )}

              {textData?.text && corrections.length === 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                  <p className="text-yellow-800">⚠️ {t('examResults.noCorrectionsForTask')}</p>
                </div>
              )}

              {Object.keys(scores).length > 0 && (
                <SingleTaskScores scores={scores} guidelineMetadata={guidelineMetadata} taskNumber={index + 1} />
              )}

              {Object.keys(feedbackTexts).length > 0 && (
                <div>
                  <h5 className="text-lg font-bold text-gray-800 mb-4">{t('examResults.detailedFeedback')}</h5>
                  <div className="space-y-4">
                    {Object.entries(feedbackTexts).map(([category, text]) => (
                      <div key={category} className="p-4 bg-white border-l-4 border-purple-400 rounded-r-xl">
                        <p className="font-semibold text-purple-800 mb-2">{category}</p>
                        <p className="text-purple-700 leading-relaxed">{text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {feedback.overallScore !== undefined && (() => {
          const maxPts = guidelineMetadata.maxPoints ?? 100;
          const displayScore = Math.min(Number(feedback.overallScore) || 0, maxPts);
          return (
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl border-2 border-purple-300 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-600 font-semibold">{t('examResults.totalScore')}</p>
                  <p className="text-3xl font-bold text-purple-800">{displayScore}/{maxPts}</p>
                </div>
                <div className="text-right">
                  <p className="text-purple-600 font-semibold">{t('examResults.status')}</p>
                  <p className={`text-2xl font-bold ${feedback.passed ? 'text-green-600' : 'text-red-600'}`}>
                    {feedback.passed ? t('examResults.passed') : t('examResults.notPassed')}
                  </p>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    );
  }

  // ── MULTI-TASK: task1/2/3Evaluation (Goethe, OSD B2/C1, TELC C1) ─────────
  if (hasTaskEvaluations) {
    const tasks = [];
    if (feedback.task1Evaluation) tasks.push({ key: 'task1', number: 1, data: feedback.task1Evaluation });
    if (feedback.task2Evaluation) tasks.push({ key: 'task2', number: 2, data: feedback.task2Evaluation });
    if (feedback.task3Evaluation) tasks.push({ key: 'task3', number: 3, data: feedback.task3Evaluation });

    const getMaxPointsForTask = (taskNumber) => {
      const taskInfo = guidelineMetadata?.taskHierarchy?.find((ti) => ti.taskNumber === taskNumber);
      return taskInfo?.points || 15;
    };

    return (
      <div className="space-y-8">
        {tasks.map(({ key, number, data }) => {
          const textData = (() => {
            if (writingData?.texts?.[`writing-task-${number}`]) return writingData.texts[`writing-task-${number}`];
            const examProvider = guidelineMetadata?.examProvider || 'default';
            const possibleKeys = [
              `${examProvider}_writing-task-${number}_text`,
              `writing-task-${number}`,
              `task${number}`,
            ];
            for (const possibleKey of possibleKeys) {
              const textEntry = Object.entries(writingData?.texts || {}).find(
                ([k]) => k === possibleKey || k.includes(`writing-task-${number}`),
              );
              if (textEntry) return textEntry[1];
            }
            return null;
          })();

          const maxPoints = getMaxPointsForTask(number);

          return (
            <div key={key} className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl border border-purple-200 p-6 space-y-6">
              <div className="flex items-center justify-between border-b border-purple-200 pb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-purple-600 text-white rounded-xl">
                    <Sparkles className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-purple-800">{t('examResults.taskNumber', { n: number })}</h4>
                    <p className="text-purple-600">{textData?.partName || `Task ${number}`}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-purple-600 text-sm">{t('examResults.points')}</p>
                  <p className="text-2xl font-bold text-purple-800">
                    {Math.min(Number(data.scores?.taskTotal) || 0, maxPoints)}/{maxPoints}
                  </p>
                </div>
              </div>

              {textData?.text && data.corrections?.length > 0 && (
                <div>
                  <h5 className="text-lg font-bold text-gray-800 mb-3">{t('examResults.yourTextWithCorrections')}</h5>
                  <TeacherCorrections originalText={textData.text} corrections={data.corrections} />
                </div>
              )}

              {data.scores && (
                <SingleTaskScores
                  scores={data.scores}
                  guidelineMetadata={feedback.guidelineMetadata || {}}
                  taskNumber={number}
                />
              )}

              {data.feedback && Object.keys(data.feedback).length > 0 && (
                <div>
                  <h5 className="text-lg font-bold text-gray-800 mb-4">{t('examResults.detailedFeedback')}</h5>
                  <div className="space-y-4">
                    {Object.entries(data.feedback).map(([category, text]) => (
                      <div key={category} className="p-4 bg-white border-l-4 border-purple-400 rounded-r-xl">
                        <p className="font-semibold text-purple-800 mb-2">{category}</p>
                        <p className="text-purple-700 leading-relaxed">{text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {data.correctedText && (
                <div>
                  <h5 className="text-lg font-bold text-gray-800 mb-4">{t('examResults.fullyCorrectedText')}</h5>
                  <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                    <p className="text-green-900 whitespace-pre-wrap leading-relaxed">
                      {sanitizeCorrectedText(data.correctedText)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {feedback.overallScore !== undefined && (() => {
          const maxPts = guidelineMetadata.maxPoints ?? 100;
          const displayScore = Math.min(Number(feedback.overallScore) || 0, maxPts);
          return (
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl border-2 border-purple-300 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-600 font-semibold">{t('examResults.totalScore')}</p>
                  <p className="text-3xl font-bold text-purple-800">{displayScore}/{maxPts}</p>
                </div>
                <div className="text-right">
                  <p className="text-purple-600 font-semibold">{t('examResults.status')}</p>
                  <p className={`text-2xl font-bold ${feedback.passed ? 'text-green-600' : 'text-red-600'}`}>
                    {feedback.passed ? t('examResults.passed') : t('examResults.notPassed')}
                  </p>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    );
  }

  // ── SINGLE-TASK (TELC B1/B2, OSD B1) ─────────────────────────────────────
  const scores = feedback.scores || {};
  const feedbackTexts = feedback.feedback || {};
  const correctedText = feedback.correctedText || '';
  const corrections = feedback.corrections || [];
  const textData = writingData?.texts?.[Object.keys(writingData?.texts || {})[0]];

  return (
    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl border border-purple-200 p-6 space-y-6">
      {textData?.text && (
        <div>
          <h5 className="text-lg font-bold text-gray-800 mb-3">
            {corrections.length > 0 ? t('examResults.yourTextWithCorrections') : t('examResults.yourSubmittedText')}
          </h5>
          {corrections.length > 0 ? (
            <TeacherCorrections originalText={textData.text} corrections={corrections} />
          ) : (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
              <p className="text-gray-900 whitespace-pre-wrap leading-relaxed">{textData.text}</p>
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
                <p className="font-semibold text-green-800">🎉 {t('examResults.noErrorsInText')}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {Object.keys(scores).length > 0 && (
        <SingleTaskScores scores={scores} guidelineMetadata={guidelineMetadata} taskNumber={null} />
      )}

      {Object.keys(feedbackTexts).length > 0 && (
        <div>
          <h5 className="text-lg font-bold text-gray-800 mb-4">{t('examResults.detailedFeedback')}</h5>
          <div className="space-y-4">
            {Object.entries(feedbackTexts).map(([category, text]) => (
              <div key={category} className="p-4 bg-white border-l-4 border-purple-400 rounded-r-xl">
                <p className="font-semibold text-purple-800 mb-2">{category}</p>
                <p className="text-purple-700 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {correctedText && (
        <div>
          <h5 className="text-lg font-bold text-gray-800 mb-4">{t('examResults.fullyCorrectedText')}</h5>
          <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
            <p className="text-green-900 whitespace-pre-wrap leading-relaxed">
              {sanitizeCorrectedText(correctedText)}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
